// Itinéraire traversant plusieurs documents — voir CLAUDE.md "Itinéraire entre documents". Pur
// (aucune dépendance à MapLibre/au DOM), testable directement, comme `pathfinding.ts` dont il
// réutilise `findRoute` tel quel pour chaque tronçon.

import type { LngLat, Shape } from "../adapters/types";
import { distance, findRoute, shapeCentroid, type RouteEndpoint } from "./pathfinding";

/** Un document parmi ceux que l'itinéraire peut traverser, réduit à ce dont le calcul a besoin. */
export interface RouteDocument {
  /** Identifiant tel que `Shape.targetDocumentId` le désigne. */
  documentId: string;
  shapes: Shape[];
}

/** Extrémité d'un itinéraire inter-documents : une extrémité ordinaire, plus le document où elle se trouve. */
export interface DocumentRouteEndpoint {
  documentId: string;
  endpoint: RouteEndpoint;
}

export interface FindRouteAcrossDocumentsOptions {
  documents: RouteDocument[];
  from: DocumentRouteEndpoint;
  to: DocumentRouteEndpoint;
  /**
   * Coût attribué au franchissement d'un passage, dans l'unité des distances calculées par
   * `pathfinding` (degrés lng/lat, voir `distance`). Départage deux passages autrement équivalents,
   * et empêche un itinéraire de multiplier les allers-retours entre documents pour économiser
   * quelques mètres. Par défaut : `0` — tous les passages se valent, seule compte la marche.
   */
  crossingCost?: number;
}

/** Portion de l'itinéraire se déroulant sur UN document, dans l'ordre de parcours. */
export interface RouteLeg {
  documentId: string;
  /** Tracé sur ce document (CRS canonique), tel que `RouteResult.coordinates` le rend. */
  coordinates: LngLat[];
  /**
   * Forme du passage emprunté pour rejoindre le tronçon suivant — absente sur le dernier tronçon,
   * qui se termine à l'arrivée demandée. C'est la forme sur laquelle le visiteur doit cliquer (ou
   * que le consommateur doit signaler) pour changer de document.
   */
  exitShapeId?: string;
  /**
   * Forme par laquelle on est arrivé sur ce document — le passage réciproque de l'`exitShapeId` du
   * tronçon précédent. Absente sur le premier tronçon.
   */
  entryShapeId?: string;
}

/**
 * Un passage : une forme qui emmène vers un autre document (`Shape.targetDocumentId`). C'est la seule
 * chose qui relie deux documents — il n'existe pas de notion de passage distincte de celle-là.
 */
interface Passage {
  documentId: string;
  shapeId: string;
  targetDocumentId: string;
  /** Position de la forme, qui sert à retrouver son homologue de l'autre côté (voir `reciprocalOf`). */
  position: LngLat;
}

/** Nœud du graphe : un point identifié sur un document. */
type NodeId = string;

function nodeIdOfShape(documentId: string, shapeId: string): NodeId {
  return `${documentId}\u0000shape\u0000${shapeId}`;
}

function nodeIdOfEndpoint(documentId: string, side: "from" | "to"): NodeId {
  return `${documentId}\u0000endpoint\u0000${side}`;
}

/** Longueur d'un tracé, somme de ses segments — la mesure qui sert de poids d'arête. */
function pathLength(coordinates: LngLat[]): number {
  let total = 0;
  for (let i = 1; i < coordinates.length; i++) total += distance(coordinates[i - 1]!, coordinates[i]!);
  return total;
}

function collectPassages(documents: RouteDocument[], options: { includeUnknownTargets?: boolean } = {}): Passage[] {
  const passages: Passage[] = [];

  for (const document of documents) {
    for (const shape of document.shapes) {
      const targetDocumentId = shape.targetDocumentId;
      // Une liaison vers un document absent de `documents` (carte supprimée, ou simplement pas
      // fournie) n'ouvre sur rien : le passage n'existe pas pour le calcul. `includeUnknownTargets`
      // le garde, pour qui veut justement les signaler (voir `documentConnectivity`).
      if (!targetDocumentId) continue;
      if (!options.includeUnknownTargets && !documents.some((d) => d.documentId === targetDocumentId)) continue;

      passages.push({
        documentId: document.documentId,
        shapeId: shape.id,
        targetDocumentId,
        position: shapeCentroid(shape),
      });
    }
  }

  return passages;
}

/**
 * Les documents qu'un itinéraire partant de `documentId` peut atteindre — lui-même inclus.
 *
 * Deux documents ne sont reliés que si un passage mène de l'un à l'autre ET qu'un passage revient
 * en sens inverse : le retour est ce qui indique où l'on ressort (voir `findRouteAcrossDocuments`),
 * sans lui on saurait aller sur un document sans savoir où l'on y arrive. La condition est donc la
 * même dans les deux sens, et un document dont il manque un des deux passages est hors d'atteinte.
 *
 * Existe pour que le consommateur ne PROPOSE pas ce que le calcul ne saura pas faire : une
 * destination injoignable ne donnerait qu'un échec, après coup. Vit ici, à côté du calcul, pour que
 * les deux ne puissent pas diverger.
 */
export function reachableDocumentIds(documents: RouteDocument[], documentId: string): Set<string> {
  const passages = collectPassages(documents);
  const hasPassage = new Set(passages.map((passage) => `${passage.documentId}\u0000${passage.targetDocumentId}`));

  const reachable = new Set<string>();
  const queue: string[] = [];

  if (documents.some((document) => document.documentId === documentId)) {
    reachable.add(documentId);
    queue.push(documentId);
  }

  while (queue.length) {
    const current = queue.shift()!;

    for (const passage of passages) {
      if (passage.documentId !== current) continue;
      if (reachable.has(passage.targetDocumentId)) continue;
      if (!hasPassage.has(`${passage.targetDocumentId}\u0000${current}`)) continue;

      reachable.add(passage.targetDocumentId);
      queue.push(passage.targetDocumentId);
    }
  }

  return reachable;
}

/** Liaison d'un document vers un autre, telle qu'on la signale — voir `documentConnectivity`. */
export interface DocumentLink {
  documentId: string;
  /** Document visé. Inconnu de `documents` dans le cas d'un `brokenLink`. */
  targetDocumentId: string;
}

/** État des liaisons d'un ensemble de documents — voir `documentConnectivity`. */
export interface DocumentConnectivity {
  /**
   * Groupes de documents qui se rejoignent, le plus grand d'abord. Plusieurs groupes : leurs
   * documents sont hors d'atteinte les uns des autres.
   */
  groups: string[][];
  /**
   * Liaisons à sens unique : un passage y mène, aucun n'en revient. Signalées à part parce qu'elles
   * ne relient RIEN — c'est le retour qui indique où l'on ressort.
   */
  oneWayLinks: DocumentLink[];
  /** Passages dont le document visé est absent de `documents` : cliquer n'ouvre rien. */
  brokenLinks: DocumentLink[];
}

/**
 * Ce que les passages relient, et ce qu'ils ratent — de quoi prévenir l'auteur d'un ensemble de
 * documents avant que ses visiteurs ne butent dessus.
 *
 * Les groupes sortent de `reachableDocumentIds` : un lien ne compte que dans les deux sens, ici
 * comme dans le calcul d'itinéraire.
 */
export function documentConnectivity(documents: RouteDocument[]): DocumentConnectivity {
  const groups: string[][] = [];
  const grouped = new Set<string>();

  for (const document of documents) {
    if (grouped.has(document.documentId)) continue;

    const group = [...reachableDocumentIds(documents, document.documentId)];

    for (const documentId of group) grouped.add(documentId);
    groups.push(group);
  }

  const passages = collectPassages(documents, { includeUnknownTargets: true });
  const known = new Set(documents.map((document) => document.documentId));
  const linked = new Set(passages.map((passage) => `${passage.documentId}\u0000${passage.targetDocumentId}`));

  const oneWayLinks: DocumentLink[] = [];
  const brokenLinks: DocumentLink[] = [];

  // Deux escaliers vers le même étage sont deux formes pour une seule liaison : c'est la liaison
  // qui est en défaut, pas chaque forme.
  for (const link of dedupeLinks(passages)) {
    if (!known.has(link.targetDocumentId)) brokenLinks.push(link);
    else if (!linked.has(`${link.targetDocumentId}\u0000${link.documentId}`)) oneWayLinks.push(link);
  }

  // Tri stable : à taille égale, les groupes gardent l'ordre des documents reçus.
  return { groups: groups.sort((a, b) => b.length - a.length), oneWayLinks, brokenLinks };
}

function dedupeLinks(passages: Passage[]): DocumentLink[] {
  const links = new Map<string, DocumentLink>();

  for (const { documentId, targetDocumentId } of passages) {
    links.set(`${documentId}\u0000${targetDocumentId}`, { documentId, targetDocumentId });
  }

  return [...links.values()];
}

/**
 * Itinéraire entre deux points pouvant se trouver sur des documents différents. Retourne la suite
 * des tronçons à parcourir, un par document traversé, ou `null` si aucun chemin n'existe.
 *
 * Le graphe est minuscule : ses nœuds sont les deux extrémités et les passages (voir `Passage`),
 * ses arêtes intra-document sont calculées par `findRoute` — inchangé, il reste le seul à savoir
 * contourner les obstacles — et ses arêtes inter-documents relient chaque passage à son RÉCIPROQUE
 * sur le document visé (voir `reciprocalOf`). C'est ce qui tient lieu de "point d'arrivée" : une
 * liaison ne désigne qu'un document, jamais un endroit sur celui-ci (voir `Shape.targetDocumentId`).
 * Un document sans passage de retour ne peut donc être ni traversé ni atteint : on saurait y aller,
 * pas où l'on y arrive.
 *
 * Les arêtes intra-document sont calculées à la demande et mémorisées : `findRoute` est coûteux
 * (grille + A*), et Dijkstra n'a pas besoin de la moitié d'entre elles sur un graphe où la plupart
 * des passages ne sont jamais atteints.
 */
export function findRouteAcrossDocuments(options: FindRouteAcrossDocumentsOptions): RouteLeg[] | null {
  const { documents, from, to, crossingCost = 0 } = options;

  const documentsById = new Map(documents.map((document) => [document.documentId, document]));
  if (!documentsById.has(from.documentId) || !documentsById.has(to.documentId)) return null;

  // Même document : rien à traverser, `findRoute` suffit et son résultat est l'unique tronçon.
  if (from.documentId === to.documentId) {
    const result = findRoute({ shapes: documentsById.get(from.documentId)!.shapes, from: from.endpoint, to: to.endpoint });
    return result ? [{ documentId: from.documentId, coordinates: result.coordinates }] : null;
  }

  const passages = collectPassages(documents);
  const passagesByDocument = new Map<string, Passage[]>();
  for (const passage of passages) {
    const list = passagesByDocument.get(passage.documentId) ?? [];
    list.push(passage);
    passagesByDocument.set(passage.documentId, list);
  }

  /** Les extrémités et passages d'un document, sous la forme d'endpoints que `findRoute` accepte. */
  function anchorsOf(documentId: string): { nodeId: NodeId; endpoint: RouteEndpoint }[] {
    const anchors: { nodeId: NodeId; endpoint: RouteEndpoint }[] = [];

    if (from.documentId === documentId) anchors.push({ nodeId: nodeIdOfEndpoint(documentId, "from"), endpoint: from.endpoint });
    if (to.documentId === documentId) anchors.push({ nodeId: nodeIdOfEndpoint(documentId, "to"), endpoint: to.endpoint });

    for (const passage of passagesByDocument.get(documentId) ?? []) {
      anchors.push({
        nodeId: nodeIdOfShape(documentId, passage.shapeId),
        endpoint: { type: "shape", shapeId: passage.shapeId },
      });
    }

    return anchors;
  }

  const anchorsByNode = new Map<NodeId, { documentId: string; endpoint: RouteEndpoint }>();
  for (const document of documents) {
    for (const anchor of anchorsOf(document.documentId)) {
      anchorsByNode.set(anchor.nodeId, { documentId: document.documentId, endpoint: anchor.endpoint });
    }
  }

  /**
   * L'autre bout d'un passage : parmi les formes du document visé qui ramènent vers celui d'où l'on
   * part, la PLUS PROCHE géographiquement. Les documents d'un même projet partagent leur système de
   * coordonnées — deux étages d'un bâtiment se superposent —, donc l'escalier d'en face est celui
   * qui tombe au même endroit. Sans ce critère, sortir par n'importe quel réciproque reviendrait à
   * monter un escalier pour redescendre par un autre, à l'opposé du bâtiment.
   *
   * Approximation assumée, et le seul point où ce module devine quelque chose : une liaison ne dit
   * pas où l'on ressort (voir `Shape.targetDocumentId`). Deux documents qui ne se superposent pas
   * (deux sites distincts) n'ont pas de "même endroit" — le plus proche reste alors un choix
   * arbitraire parmi les retours possibles, mais un choix, pas une incohérence.
   */
  function reciprocalOf(passage: Passage): Passage | null {
    const candidates = (passagesByDocument.get(passage.targetDocumentId) ?? []).filter(
      (candidate) => candidate.targetDocumentId === passage.documentId,
    );

    let nearest: Passage | null = null;
    let nearestDistance = Infinity;

    for (const candidate of candidates) {
      const candidateDistance = distance(passage.position, candidate.position);
      if (candidateDistance < nearestDistance) {
        nearest = candidate;
        nearestDistance = candidateDistance;
      }
    }

    return nearest;
  }

  const legCache = new Map<string, { coordinates: LngLat[]; length: number } | null>();

  /** Tronçon entre deux points d'un même document, calculé une seule fois par paire. */
  function legBetween(nodeA: NodeId, nodeB: NodeId): { coordinates: LngLat[]; length: number } | null {
    const key = `${nodeA}\u0001${nodeB}`;
    if (legCache.has(key)) return legCache.get(key)!;

    const a = anchorsByNode.get(nodeA)!;
    const b = anchorsByNode.get(nodeB)!;
    const result = findRoute({ shapes: documentsById.get(a.documentId)!.shapes, from: a.endpoint, to: b.endpoint });
    const leg = result ? { coordinates: result.coordinates, length: pathLength(result.coordinates) } : null;

    legCache.set(key, leg);
    return leg;
  }

  const startNode = nodeIdOfEndpoint(from.documentId, "from");
  const goalNode = nodeIdOfEndpoint(to.documentId, "to");

  const best = new Map<NodeId, number>([[startNode, 0]]);
  const cameFrom = new Map<NodeId, { node: NodeId; crossing: boolean }>();
  const settled = new Set<NodeId>();

  for (;;) {
    // File de priorité par balayage linéaire : le graphe compte quelques dizaines de nœuds au plus
    // (les passages d'un projet), un tas binaire n'y gagnerait rien de mesurable.
    let current: NodeId | null = null;
    let currentCost = Infinity;
    for (const [node, cost] of best) {
      if (!settled.has(node) && cost < currentCost) {
        current = node;
        currentCost = cost;
      }
    }

    if (current === null) return null;
    if (current === goalNode) break;

    settled.add(current);

    const { documentId } = anchorsByNode.get(current)!;

    // Marcher jusqu'à un autre point du même document.
    for (const anchor of anchorsOf(documentId)) {
      if (anchor.nodeId === current || settled.has(anchor.nodeId)) continue;

      const leg = legBetween(current, anchor.nodeId);
      if (!leg) continue;

      const cost = currentCost + leg.length;
      if (cost < (best.get(anchor.nodeId) ?? Infinity)) {
        best.set(anchor.nodeId, cost);
        cameFrom.set(anchor.nodeId, { node: current, crossing: false });
      }
    }

    // Franchir un passage : on ressort par le passage réciproque du document visé.
    const passage = (passagesByDocument.get(documentId) ?? []).find(
      (candidate) => nodeIdOfShape(documentId, candidate.shapeId) === current,
    );

    if (passage) {
      const reciprocal = reciprocalOf(passage);

      if (reciprocal) {
        const nodeId = nodeIdOfShape(reciprocal.documentId, reciprocal.shapeId);
        const cost = currentCost + crossingCost;

        if (!settled.has(nodeId) && cost < (best.get(nodeId) ?? Infinity)) {
          best.set(nodeId, cost);
          cameFrom.set(nodeId, { node: current, crossing: true });
        }
      }
    }
  }

  // Remonter le chemin, puis le découper en tronçons : un franchissement termine le tronçon courant
  // et en ouvre un sur le document suivant. `crossing` qualifie l'ARÊTE qui mène au nœud, pas le
  // nœud lui-même — d'où les deux tableaux parallèles plutôt qu'un seul de paires.
  const nodes: NodeId[] = [startNode];
  const crossings: boolean[] = [false];
  for (let node = goalNode; node !== startNode; ) {
    const previous = cameFrom.get(node)!;
    nodes.splice(1, 0, node);
    crossings.splice(1, 0, previous.crossing);
    node = previous.node;
  }

  const legs: RouteLeg[] = [];
  let coordinates: LngLat[] = [];
  let entryShapeId: string | undefined;

  for (let i = 1; i < nodes.length; i++) {
    const previousNode = nodes[i - 1]!;
    const node = nodes[i]!;
    const crossing = crossings[i]!;

    if (crossing) {
      const exit = anchorsByNode.get(previousNode)!;
      const exitPassage = (passagesByDocument.get(exit.documentId) ?? []).find(
        (candidate) => nodeIdOfShape(exit.documentId, candidate.shapeId) === previousNode,
      )!;

      legs.push({
        documentId: exit.documentId,
        coordinates,
        exitShapeId: exitPassage.shapeId,
        ...(entryShapeId !== undefined ? { entryShapeId } : {}),
      });

      const entry = (passagesByDocument.get(anchorsByNode.get(node)!.documentId) ?? []).find(
        (candidate) => nodeIdOfShape(candidate.documentId, candidate.shapeId) === node,
      )!;

      coordinates = [];
      entryShapeId = entry.shapeId;
      continue;
    }

    const leg = legBetween(previousNode, node)!;
    // Le premier point du tronçon suivant répète le dernier du précédent (même position, les deux
    // appels à `findRoute` partant/arrivant au même endroit) — retiré pour ne pas doubler un sommet.
    coordinates = coordinates.length ? [...coordinates, ...leg.coordinates.slice(1)] : leg.coordinates;
  }

  legs.push({
    documentId: to.documentId,
    coordinates,
    ...(entryShapeId !== undefined ? { entryShapeId } : {}),
  });

  return legs;
}
